import {
    all,
    call,
    fork,
    put,
    select,
    takeLatest,
    PutEffect
} from 'redux-saga/effects';
import { AxiosResponse, AxiosError } from 'axios';
import keyBy from 'lodash.keyby';
import get from 'lodash.get';
import { SagaIterator } from 'redux-saga';
import { DeepPartial } from 'redux';
import diff from 'lodash.difference';

// Global Actions
// @ts-ignore
import { updateQueryString } from 'services/router/actions';
// @ts-ignore
import { resetSelectedTableCells } from 'services/selectedTableCells/actions';
// @ts-ignore
import { enqueueSnackbar } from 'services/notifications/actions';

// Global Utils
// @ts-ignore
import axiosPoll from 'utils/axiosPoll';
import {
    takeLatestWithCancel,
    patchRequest,
    postRequest,
    putRequest
} from 'utils/redux-saga';

// Global Selectors
import { selectSpraySchemaId } from 'services/schemas/selectors';
import {
    EDIT_SPRAY_EVENT_MODAL,
    CREATE_SPRAY_EVENT_MODAL,
    COPY_SPRAY_EVENT_MODAL,
    UNCOMPLETE_SPRAY_EVENT_MODAL
} from 'scenes/Spray/scenes/Diary/constants';
import { COMPLETE_SPRAY_EVENT_MODAL } from 'scenes/Spray/scenes/Diary/components/CompleteSprayEventModal/constants';
import { sortEventJobByJobTypes } from 'utils/events';

// Local Types
import omit from 'lodash.omit';
import {
    RequestCreateSprayEventAction,
    RequestDeleteSprayEventAction,
    RequestUpdateSprayEventAction,
    SprayEvent,
    RequestCompleteSprayEventsAction,
    RequestUncompleteSprayEventAction,
    SprayEventJob
} from './types';

// Local Constants
import {
    SPRAY_EVENTS_LOAD_REQUESTED,
    SPRAY_EVENTS_LOAD_CANCELED,
    SPRAY_EVENT_CREATE_REQUESTED,
    SPRAY_EVENT_DELETE_REQUESTED,
    SPRAY_EVENT_UPDATE_REQUESTED,
    SPRAY_EVENTS_COMPLETE_REQUESTED,
    SPRAY_EVENT_UNCOMPLETE_REQUESTED
} from './constants';

// Local Actions
import {
    succeededLoadSprayEvents,
    failedLoadSprayEvents,
    succeededCreateSprayEvent,
    failedCreateSprayEvent,
    setSprayEvents,
    deleteSprayEvents,
    succeededDeleteSprayEvent,
    failedDeleteSprayEvent,
    succeededUpdateSprayEvent,
    failedUpdateSprayEvent,
    setSprayEvent,
    failedCompleteSprayEvents,
    succeededCompleteSprayEvents,
    succeededUncompleteSprayEvent,
    failedUncompleteSprayEvent
} from './actions';

// Local Selectors
import { selectLoadSprayEventsParams } from './params';
import { selectSprayEvents, selectFilteredSprayEventsUuids } from './selectors';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* loadSprayEventsSucceeded(
    response: AxiosResponse
): SagaIterator {
    // Succeeded Load Spray Events
    const currentUuids = yield select(selectFilteredSprayEventsUuids);
    const newUuids = response.data.map(({ uuid }: SprayEvent) => uuid);
    const deletedUuids: string[] = diff(currentUuids, newUuids);
    const tasks: PutEffect<any>[] = [
        put(succeededLoadSprayEvents(response)),
        put(setSprayEvents(keyBy(response.data, 'uuid')))
    ];

    if (deletedUuids.length) {
        tasks.push(put(deleteSprayEvents(deletedUuids)));
    }

    yield all(tasks);
}

export function* loadSprayEventsFailed(error: AxiosError): SagaIterator {
    yield put(failedLoadSprayEvents(error));
}

export function* loadSprayEvents(): SagaIterator {
    // Select params
    const params = yield select(selectLoadSprayEventsParams);

    yield call(axiosPoll, {
        config: {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/Events`,
            params
        },
        succeeded: loadSprayEventsSucceeded,
        failed: loadSprayEventsFailed
    });
}

export function* watchSprayEventsLoadRequested(): SagaIterator {
    yield takeLatestWithCancel(
        SPRAY_EVENTS_LOAD_REQUESTED,
        SPRAY_EVENTS_LOAD_CANCELED,
        loadSprayEvents
    );
}

export function* createSprayEvent(
    action: RequestCreateSprayEventAction
): SagaIterator {
    const { payload } = action;

    // Select Spray Schema ID
    const spraySchemaId = yield select(selectSpraySchemaId);

    try {
        const {
            entityId,
            startDate,
            endDate,
            sprayEntityIds,
            sprayProducts,
            sprayApplicationRate,
            sprayMethod,
            sprayWaterSourceId,
            justification,
            technicalPersonName,
            isFinalSpray,
            isWeedSpray,
            applicationType,
            sprayEventOperations
        } = payload;

        const values = {
            entityId,
            startDate,
            endDate,
            sprayEvent: {
                sprayEventOperations,
                applicationType,
                sprayEntityIds,
                sprayProducts,
                sprayMethod,
                sprayWaterSourceId,
                justification,
                technicalPersonName,
                isFinalSpray,
                isWeedSpray,
                applicationRate: sprayApplicationRate
            },
            jobs: [
                {
                    jobType: 'INSPECTION'
                },
                {
                    jobType: 'TIMESHEET'
                }
            ],
            schemaId: spraySchemaId
        };

        const response = yield call(postRequest, 'Events', values);

        // Check if response exists to satisfy TypeScript
        if (response) {
            yield all([
                // Set SprayEvent
                put(setSprayEvent(response.data)),
                // Succeeded Create Spray Event
                put(succeededCreateSprayEvent(response)),
                // Update Query String
                put(
                    updateQueryString({
                        [CREATE_SPRAY_EVENT_MODAL]: undefined,
                        [COPY_SPRAY_EVENT_MODAL]: undefined,
                        sprayEventUuid: undefined
                    })
                )
            ]);

            // Success Snackbar
            yield put(
                enqueueSnackbar({
                    message: 'Spray event created successfully',
                    options: { variant: 'success' }
                })
            );
        }
    } catch (error) {
        yield put(failedCreateSprayEvent(error));
    }
}

export function* completeSprayEvent(
    action: RequestCompleteSprayEventsAction
): SagaIterator {
    const { payload } = action;
    const { uuids, weatherUuids, disposalUuids } = payload;

    try {
        const sprayEvents = yield select(selectSprayEvents);
        const completedAt = new Date();
        const updatePayload = uuids.reduce(
            (result: DeepPartial<SprayEvent>[], uuid) => {
                const sprayEventJobs: SprayEventJob[] = get(
                    sprayEvents,
                    [uuid, 'jobs'],
                    []
                );

                const jobs = sprayEventJobs.map(({ jobType }) => ({
                    jobType,
                    completedAt
                }));
                let sprayEvent;

                if (weatherUuids || disposalUuids) {
                    sprayEvent = {
                        weather: weatherUuids ? weatherUuids[uuid] : undefined,
                        disposalMethod: disposalUuids
                            ? disposalUuids[uuid]
                            : undefined
                    };
                }

                return [
                    ...result,
                    {
                        uuid,
                        jobs,
                        sprayEvent
                    }
                ];
            },
            []
        );

        const response = yield call(putRequest, 'Events/bulk', {
            update: updatePayload
        });

        const updatedSprayEvents = response.data.update;

        if (updatedSprayEvents && updatedSprayEvents.length) {
            yield all([
                put(succeededCompleteSprayEvents()),
                put(setSprayEvents(keyBy(updatedSprayEvents, 'uuid'))),
                put(
                    updateQueryString({
                        [COMPLETE_SPRAY_EVENT_MODAL]: undefined
                    })
                ),
                put(
                    enqueueSnackbar({
                        message: `Spray ${
                            updatedSprayEvents.length === 1 ? 'event' : 'events'
                        } completed successfully`,
                        options: { variant: 'success' }
                    })
                )
            ]);
        } else if (response.data.errors) {
            yield put(failedCompleteSprayEvents(response.data.errors));
        }
    } catch (error) {
        yield put(failedCompleteSprayEvents(error));
    }
}

export function* patchSprayEvent(
    action: RequestUpdateSprayEventAction
): SagaIterator {
    const { payload } = action;

    try {
        const {
            sprayEventUuid,
            startDate,
            endDate,
            sprayEntityIds,
            sprayProducts,
            sprayApplicationRate,
            sprayMethod,
            sprayWaterSourceId,
            justification,
            technicalPersonName,
            isFinalSpray,
            isWeedSpray,
            applicationType,
            sprayEventOperations
        } = payload;

        const sprayEvents = yield select(selectSprayEvents);
        const sprayEventId = get(sprayEvents, [sprayEventUuid, 'id']);

        const values = {
            startDate,
            endDate,
            sprayEvent: {
                sprayEventOperations,
                applicationType,
                sprayEntityIds,
                sprayProducts,
                sprayMethod,
                sprayWaterSourceId,
                justification,
                technicalPersonName,
                isFinalSpray,
                isWeedSpray,
                applicationRate: sprayApplicationRate
            },
            jobs: [
                {
                    jobType: 'INSPECTION'
                },
                {
                    jobType: 'TIMESHEET'
                }
            ]
        };

        const response = yield call(
            patchRequest,
            'Events',
            values,
            sprayEventId
        );

        // Check if response exists to satisfy TypeScript
        if (response) {
            const { data } = response;

            const jobs = sortEventJobByJobTypes(data.jobs);

            yield all([
                // Set SprayEvent
                put(setSprayEvent({ ...data, jobs })),
                // Succeeded Update Spray Event
                put(succeededUpdateSprayEvent(response)),
                // Update Query String
                put(
                    updateQueryString({
                        [EDIT_SPRAY_EVENT_MODAL]: undefined,
                        sprayEventUuid: undefined
                    })
                )
            ]);

            // Success Snackbar
            yield put(
                enqueueSnackbar({
                    message: 'Spray event updated successfully',
                    options: { variant: 'success' }
                })
            );
        }
    } catch (error) {
        yield put(failedUpdateSprayEvent(error));
    }
}

export function* deleteSprayEvent(
    action: RequestDeleteSprayEventAction
): SagaIterator {
    const {
        payload: { uuids }
    } = action;

    try {
        const response = yield call(putRequest, 'Events/bulk', {
            delete: uuids
        });

        const { delete: deletedSprayEvents, errors } = response.data;

        if (deletedSprayEvents && deletedSprayEvents.length) {
            const deletedUuids = deletedSprayEvents.map(
                (value: SprayEvent) => value.uuid
            );

            yield put(succeededDeleteSprayEvent(deletedUuids));
        }

        if (errors && errors.length) {
            yield put(failedDeleteSprayEvent(response));
        } else {
            yield all([
                put(
                    updateQueryString({
                        deleteSprayEventModal: undefined,
                        selectedEventId: undefined
                    })
                ),
                put(resetSelectedTableCells())
            ]);

            yield put(
                enqueueSnackbar({
                    message: `Spray ${
                        deletedSprayEvents.length > 1 ? 'events' : 'event'
                    } deleted successfully`,
                    options: { variant: 'success' }
                })
            );
        }
    } catch (error) {
        yield put(failedDeleteSprayEvent(error));
    }
}

export function* uncompleteSprayEvent(
    action: RequestUncompleteSprayEventAction
): SagaIterator {
    const { uuid } = action.payload;

    try {
        const sprayEvents = yield select(selectSprayEvents);
        const sprayEventJobs: SprayEventJob[] = get(
            sprayEvents,
            [uuid, 'jobs'],
            []
        );
        const isWeedSpray: boolean = get(
            sprayEvents,
            [uuid, 'sprayEvent', 'isWeedSpray'],
            false
        );
        const jobs = sprayEventJobs.map(({ jobType }) => ({
            jobType,
            completedAt: null
        }));

        const updatePayload = [
            {
                uuid,
                jobs,
                sprayEvent: isWeedSpray
                    ? undefined
                    : {
                          weather: null,
                          disposalMethod: null
                      }
            }
        ];

        const response = yield call(putRequest, 'Events/bulk', {
            update: updatePayload
        });

        const updatedSprayEvents = response.data.update;

        if (updatedSprayEvents && updatedSprayEvents.length) {
            yield all([
                put(succeededUncompleteSprayEvent(response)),
                put(setSprayEvents(keyBy(updatedSprayEvents, 'uuid'))),
                put(
                    updateQueryString({
                        [UNCOMPLETE_SPRAY_EVENT_MODAL]: undefined,
                        [EDIT_SPRAY_EVENT_MODAL]: undefined,
                        sprayEventUuid: undefined
                    })
                ),
                put(
                    enqueueSnackbar({
                        message: `Spray event uncompleted successfully`,
                        options: { variant: 'success' }
                    })
                )
            ]);
        } else if (response.data.errors) {
            yield put(failedUncompleteSprayEvent(response));
        }
    } catch (error) {
        yield put(failedUncompleteSprayEvent(error));
    }
}

export function* watchSprayEventDeleteRequested(): SagaIterator {
    yield takeLatest(SPRAY_EVENT_DELETE_REQUESTED, deleteSprayEvent);
}

export function* watchSprayEventCreateRequested(): SagaIterator {
    yield takeLatest(SPRAY_EVENT_CREATE_REQUESTED, createSprayEvent);
}

export function* watchSprayEventUpdateRequested(): SagaIterator {
    yield takeLatest(SPRAY_EVENT_UPDATE_REQUESTED, patchSprayEvent);
}

export function* watchSprayEventCompleteRequested(): SagaIterator {
    yield takeLatest(SPRAY_EVENTS_COMPLETE_REQUESTED, completeSprayEvent);
}

export function* watchSprayEventUncompleteRequested(): SagaIterator {
    yield takeLatest(SPRAY_EVENT_UNCOMPLETE_REQUESTED, uncompleteSprayEvent);
}

export default function* sprayEventsSagas(): SagaIterator {
    yield all([
        fork(watchSprayEventsLoadRequested),
        fork(watchSprayEventCreateRequested),
        fork(watchSprayEventDeleteRequested),
        fork(watchSprayEventUpdateRequested),
        fork(watchSprayEventCompleteRequested),
        fork(watchSprayEventUncompleteRequested)
    ]);
}
